import type { InkTool, ShapeKind, ToolKind } from "@deckloom/player";

const COLORS = ["#e11d48", "#2563eb", "#16a34a", "#f97316", "#facc15", "#111827", "#ffffff"];
const WIDTHS: { label: string; value: number }[] = [{ label: "thin", value: 1.5 }, { label: "medium", value: 3 }, { label: "thick", value: 6 }, { label: "marker", value: 18 }];
const SHAPES: { kind: ShapeKind; label: string; icon: string }[] = [
  { kind: "rect", label: "rectangle", icon: "▭" },
  { kind: "ellipse", label: "ellipse", icon: "◯" },
  { kind: "line", label: "line", icon: "╱" },
  { kind: "arrow", label: "arrow", icon: "➚" },
];
const SIZES = [12, 18, 24, 36];

export interface ToolbarProps {
  /** active tool, null when drawing is off */
  tool: InkTool | null;
  /** last used options (shown even when the tool is off) */
  opts: InkTool;
  onPick: (kind: ToolKind) => void;
  onChange: (patch: Partial<InkTool>) => void;
  onShape: (shape: ShapeKind) => void;
  onUndo: () => void;
  onClear?: () => void;
  /** draft mode: drawings stay private until published (left out where there is no room, e.g. the local preview) */
  draftMode?: boolean;
  /** unpublished drawings on this page */
  draftCount?: number;
  onDraftMode?: () => void;
  onPublish?: () => void;
  onDiscard?: () => void;
}

/** Annotation tools: one draw tool (pen and marker differ only in colour, opacity and width), shapes, text, eraser. */
export function Toolbar({ tool, opts, onPick, onChange, onShape, onUndo, onClear, draftMode, draftCount, onDraftMode, onPublish, onDiscard }: ToolbarProps) {
  const kind = tool?.kind;
  const btn = (k: ToolKind, label: string, title: string) => (
    <button className={kind === k ? "on" : ""} onClick={() => onPick(k)} title={title}>{label}</button>
  );
  const usesStroke = kind === "draw" || kind === "shape";
  return (
    <div className="tools">
      {btn("draw", "Draw", "draw (p: pen, m: marker)")}
      {btn("shape", "Shape", "shapes (s)")}
      {btn("text", "Text", "text (t)")}
      {btn("eraser", "Eraser", "eraser: click an annotation (e)")}
      <span className="sep" />
      <button onClick={onUndo} title="undo last annotation (ctrl+z)">Undo</button>
      {onClear && <button onClick={onClear} title="clear this page">Clear</button>}
      {onDraftMode && (
        <>
          <span className="sep" />
          <button className={draftMode ? "on" : ""} onClick={onDraftMode} title="draft mode (d): new drawings stay private until you publish them">Draft</button>
        </>
      )}
      {(draftCount ?? 0) > 0 && (
        <>
          <button className="publish" onClick={onPublish} title="show this page's drafts to everyone">Publish ({draftCount})</button>
          <button onClick={onDiscard} title="throw away this page's drafts">Discard</button>
        </>
      )}

      {tool && kind !== "eraser" && (
        <div className="tool-opts">
          {kind === "shape" && (
            <span className="group">
              {SHAPES.map((s) => (
                <button key={s.kind} className={opts.shape === s.kind ? "on" : ""} onClick={() => onShape(s.kind)} title={s.label}>{s.icon}</button>
              ))}
              <button className={opts.fill ? "on" : ""} onClick={() => onChange({ fill: !opts.fill })} title="filled">fill</button>
            </span>
          )}
          <span className="group swatches">
            {COLORS.map((hex) => (
              <button
                key={hex}
                className={`swatch${opts.color.hex === hex ? " on" : ""}`}
                style={{ background: hex }}
                onClick={() => onChange({ color: { hex, alpha: opts.color.alpha } })}
                title={hex}
              />
            ))}
          </span>
          {usesStroke && (
            <span className="group">
              {WIDTHS.map((w) => (
                <button key={w.value} className={opts.width === w.value ? "on" : ""} onClick={() => onChange({ width: w.value, color: { ...opts.color, alpha: w.value >= 18 ? 0.4 : opts.color.alpha } })} title={w.label}>
                  <i className="dot" style={{ width: Math.min(16, 4 + w.value), height: Math.min(16, 4 + w.value) }} />
                </button>
              ))}
              <button className={opts.color.alpha < 1 ? "on" : ""} onClick={() => onChange({ color: { ...opts.color, alpha: opts.color.alpha < 1 ? 1 : 0.4 } })} title="translucent (marker)">
                {opts.color.alpha < 1 ? "40%" : "100%"}
              </button>
            </span>
          )}
          {kind === "text" && (
            <span className="group">
              {SIZES.map((n) => (
                <button key={n} className={opts.fontSize === n ? "on" : ""} onClick={() => onChange({ fontSize: n })}>{n}</button>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
